import { useCreatePackage } from "admin/finance/hooks/useCreatePackage";
import { IPackage } from "admin/finance/types";
import { Form, Input, InputNumber, Modal, Radio, Checkbox } from "antd";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { QUERY_KEY_FOR_PACKAGE } from "../hooks/useGetPackage";
import { useState } from "react";

export const AddPackage = ({ open, handleClose }: IdentifierProps) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreatePackage();
  const [useEnterprise, setUseEnterprise] = useState(false);
  const [accountType, setAccountType] = useState<string | undefined>();

  const handleAccountTypeChange = (e: any) => {
    setAccountType(e.target.value);
    if (e.target.value !== "recruiter") {
      setUseEnterprise(false);
    }
  };

  const handleUseEnterpriseChange = (e: any) => {
    setUseEnterprise(e.target.checked);
  };

  const handleSubmit = (data: any) => {
    const props: IPackage = {
      name: data.name,
      description: data.description,
      monthly: data.monthly,
      quarterly: data.quarterly,
      biannually: data.biannually,
      annually: data.annually,
      account_type: data.account_type,
      token,
      use_enterprise: data.use_enterprise,
      enterprise_discount: data.enterprise_discount,
      sort_order: data.sort_order ?? 0,
    };

    mutate(props, {
      onError: (err: any) => {
        const res = err.response?.data;
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (res?.message && typeof res.message === "string") {
          errorDescription = res.message;
        }
        if (res?.errors && typeof res.errors === "object") {
          const first = Object.values(res.errors)[0];
          if (Array.isArray(first) && first[0]) {
            errorDescription = String(first[0]);
          }
        }
        if (res?.error) {
          errorTitle = "Error: " + res.error;
          errorDescription = res.description || errorDescription;
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Package Created Successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_PACKAGE],
        });
        handleClose();
      },
    });
  };

  return (
    <Modal
      title="Add Package"
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width="45rem"
      style={{ top: 10 }}
    >
      <div>
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          form={form}
          initialValues={{ is_current: false, sort_order: 0 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <Form.Item name="name" label="Name" rules={generalValidationRules}>
              <Input placeholder="Enter Package Name" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={generalValidationRules}
            >
              <Input placeholder="Enter Package Description" />
            </Form.Item>

            <Form.Item
              name="sort_order"
              label="Sort order"
              tooltip="Lower numbers are listed first on pricing and in admin."
            >
              <InputNumber<number>
                className="w-full"
                min={0}
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              name="monthly"
              label="Monthly amount"
              rules={generalValidationRules}
              className="col-span-2"
            >
              <InputNumber<number>
                className="w-full sm:w-auto"
                formatter={(value) =>
                  `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value?.replace(/\₦\s?|(,*)/g, "") as unknown as number
                }
              />
            </Form.Item>

            <Form.Item
              name="quarterly"
              label="Quarterly amount"
              rules={generalValidationRules}
              className="col-span-2"
            >
              <InputNumber<number>
                className="w-full sm:w-auto"
                formatter={(value) =>
                  `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value?.replace(/\₦\s?|(,*)/g, "") as unknown as number
                }
              />
            </Form.Item>

            <Form.Item
              name="biannually"
              label="Biannual amount"
              rules={generalValidationRules}
              className="col-span-2"
            >
              <InputNumber<number>
                className="w-full sm:w-auto"
                formatter={(value) =>
                  `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value?.replace(/\₦\s?|(,*)/g, "") as unknown as number
                }
              />
            </Form.Item>

            <Form.Item
              name="annually"
              label="Annual amount"
              rules={generalValidationRules}
              className="col-span-2"
            >
              <InputNumber<number>
                className="w-full sm:w-auto"
                formatter={(value) =>
                  `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  value?.replace(/\₦\s?|(,*)/g, "") as unknown as number
                }
              />
            </Form.Item>

            <Form.Item
              name="account_type"
              label="Account Type"
              className="-mt-3 col-span-2"
              rules={generalValidationRules}
            >
              <Radio.Group onChange={handleAccountTypeChange}>
                <Radio value="talent">Talent</Radio>
                <Radio value="recruiter">Recruiter</Radio>
                <Radio value="enterprise">Enterprise</Radio>
              </Radio.Group>
            </Form.Item>

            {accountType === "recruiter" && (
              <Form.Item
                name="use_enterprise"
                valuePropName="checked"
                className="col-span-2"
              >
                <Checkbox onChange={handleUseEnterpriseChange}>
                  Available to Enterprise
                </Checkbox>
              </Form.Item>
            )}

            {useEnterprise && (
              <Form.Item
                name="enterprise_discount"
                label="Enterprise Discount in Percentage"
                rules={generalValidationRules}
                className="col-span-2"
              >
                <InputNumber<number>
                  className="w-full sm:w-auto"
                  min={0}
                  max={100}
                  formatter={(value) => `${value}%`}
                  parser={(value) =>
                    value?.replace("%", "") as unknown as number
                  }
                />
              </Form.Item>
            )}
          </div>
          <div className="flex">
            <AppButton type="submit" isLoading={isLoading} />
          </div>
        </Form>
      </div>
    </Modal>
  );
};
